import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';
import { useAdminUsers } from '@reflex/ui/client/api/admin';
import { Badge } from '@reflex/ui/components/ui/badge';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { SearchInput } from '@reflex/ui/components/shared/SearchInput';
import { DataTablePagination } from '@reflex/ui/components/shared/DataTablePagination';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { AdminSubPageLayout } from '@reflex/ui/components/layout/AdminSubPageLayout';
import { useSearchPagination } from '@/hooks/ui/useSearchPagination';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { formatDateOnly, getProviderLabel } from '@reflex/ui/lib/format';
import type { User } from '@/lib/types';
import { getUserDisplayName } from '@/lib/agent-utils';
import { ROUTES } from '@/lib/routes';

const DEFAULT_PAGE_SIZE = 20;

const userColumns: DataTableColumn<User>[] = [
  {
    header: 'User',
    cell: (user) => (
      <Link
        to={ROUTES.admin.user(user.id)}
        className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
      >
        <UserAvatar
          userName={getUserDisplayName(user)}
          avatarUrl={user.avatarUrl ?? undefined}
          size="md"
        />
        <div className="min-w-0">
          <p className="truncate font-medium">{getUserDisplayName(user)}</p>
          {user.name && <p className="truncate text-xs text-muted-foreground">@{user.login}</p>}
        </div>
      </Link>
    ),
  },
  {
    header: 'Email',
    cell: (user) => user.email || <span className="italic text-muted-foreground">—</span>,
    cellClassName: 'text-muted-foreground',
  },
  {
    header: 'Provider',
    cell: (user) => <Badge variant="outline">{getProviderLabel(user.providerId)}</Badge>,
  },
  {
    header: 'Joined',
    cell: (user) => formatDateOnly(user.createdAt),
    cellClassName: 'text-xs text-muted-foreground',
  },
];

export function AdminUsersPanel() {
  const { page, pageSize, q, request, setSearch, setPage, setPageSize } = useSearchPagination({
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const { data, isLoading, isError, isFetching } = useAdminUsers(request);

  const users = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasSearch = request.q.length > 0;

  return (
    <SectionCard
      title="Registered Users"
      description={
        isLoading
          ? 'Loading…'
          : `${total.toLocaleString()} user${total !== 1 ? 's' : ''} registered`
      }
      icon={<Users />}
      contentClassName="space-y-3 pt-0"
    >
      <SearchInput
        value={q}
        onValueChange={setSearch}
        placeholder="Search by name, email, or ID…"
        aria-label="Search users"
        data-testid="admin-users-search"
      />
      <AsyncContent isLoading={isLoading} isError={isError} errorMessage="Failed to load users.">
        {total === 0 ? (
          <EmptyState
            variant="bordered"
            icon={<Users className="h-8 w-8" />}
            title={hasSearch ? 'No users match your search' : 'No users yet'}
            description={
              hasSearch
                ? 'Try a different name, email, or ID.'
                : 'Users will appear here once they sign in'
            }
          />
        ) : (
          <div className="overflow-hidden rounded-xl border border-border">
            <DataTable<User>
              data={users}
              columns={userColumns}
              rowKey={(user) => user.id}
              className="rounded-none border-0"
            />
            <DataTablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              itemLabel="user"
              isLoading={isFetching}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        )}
      </AsyncContent>
    </SectionCard>
  );
}

export function AdminUsersPage() {
  return (
    <AdminSubPageLayout title="Users" description="All registered users in the system">
      <AdminUsersPanel />
    </AdminSubPageLayout>
  );
}
